/**
 * BulkOfferModal Component
 *
 * Modal for bulk assigning, removing, OR replacing an offer to/from all users of a partner (or all partners).
 * Features:
 * - Mode toggle: Assign, Remove, or Replace
 * - Select a reward from active rewards in the catalog
 * - Set display priority (assign mode only)
 * - Override fields for availability configuration (assign/replace modes)
 * - Option to add/remove the reward from partner default_offers
 * - Option to apply to ALL partners (when not scoped to a single partner)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button } from '@/components/common/Modal';
import { Select, Input, TextArea } from '@/components/common/FormField';
import { partnerService } from '@/app/services/partnerService';
import { rewardService } from '@/app/services/rewardService';
import type {
  BulkAssignOfferRequest,
  BulkAssignOfferResponse,
  BulkRemoveOfferRequest,
  BulkRemoveOfferResponse,
  BulkReplaceOfferRequest,
  BulkReplaceOfferResponse,
} from '@/app/types/partners';
import type { RewardResponse } from '@/app/types/rewards';
import { logger } from '@/app/utils/logger';
import './BulkAssignOfferModal.scss';

type OperationMode = 'assign' | 'remove' | 'replace';

interface BulkOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (
    result: BulkAssignOfferResponse | BulkRemoveOfferResponse | BulkReplaceOfferResponse
  ) => void;
  /** Partner to assign/remove offers to/from. If null, shows "all partners" option */
  partnerId?: number | null;
  /** Partner name for display purposes */
  partnerName?: string;
  /** Initial mode - defaults to 'assign' */
  initialMode?: OperationMode;
}

export const BulkAssignOfferModal: React.FC<BulkOfferModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  partnerId,
  partnerName,
  initialMode = 'assign',
}) => {
  const [mode, setMode] = useState<OperationMode>(initialMode);
  const [formData, setFormData] = useState<{
    rewardId: string;
    sourceRewardId: string; // For replace mode
    priority: string;
    updateDefaultOffers: boolean;
    applyToAllPartners: boolean;
    // Override fields for assign/replace modes
    useOverrides: boolean;
    availableOverride: boolean | null;
    overrideReason: string;
    showMessageOnCard: boolean;
    preserveContext: boolean; // For replace mode
  }>({
    rewardId: '',
    sourceRewardId: '',
    priority: '1',
    updateDefaultOffers: false,
    applyToAllPartners: false,
    useOverrides: false,
    availableOverride: null,
    overrideReason: '',
    showMessageOnCard: false,
    preserveContext: true,
  });

  const [rewards, setRewards] = useState<RewardResponse[]>([]);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);

  // Reset mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  // Fetch active rewards when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchRewards = async () => {
        setLoadingRewards(true);
        try {
          const response = await rewardService.queryRewards({ status: 'ACTIVE', limit: 1000 });
          setRewards(response.rewards);
        } catch (err) {
          logger.error('Failed to fetch rewards for bulk operation', { error: err });
          setError('Failed to load rewards');
        } finally {
          setLoadingRewards(false);
        }
      };
      fetchRewards();
    }
  }, [isOpen]);

  const rewardOptions = [
    { value: '', label: loadingRewards ? 'Loading rewards...' : 'Select a reward...' },
    ...rewards
      .sort((a, b) => a.reward_name.localeCompare(b.reward_name))
      .map((r) => {
        // Build label with distinguishing information
        const parts: string[] = [];

        // Add provider if available (from redemption_config)
        if (r.redemption_config?.provider) {
          parts.push(`(${r.redemption_config.provider})`);
        }

        // Add key/variant info
        parts.push(`${r.reward_key}/${r.reward_variant}`);

        return {
          value: r.reward_id,
          label: `${r.reward_name} ${parts.join(' ')}`,
        };
      }),
  ];

  const selectedReward = rewards.find((r) => r.reward_id === formData.rewardId);

  const handleChange = useCallback((field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setConfirmStep(false);
  }, []);

  const handleModeChange = useCallback((newMode: OperationMode) => {
    setMode(newMode);
    setError(null);
    setConfirmStep(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!saving) {
      setFormData({
        rewardId: '',
        sourceRewardId: '',
        priority: '1',
        updateDefaultOffers: false,
        applyToAllPartners: false,
        useOverrides: false,
        availableOverride: null,
        overrideReason: '',
        showMessageOnCard: false,
        preserveContext: true,
      });
      setError(null);
      setConfirmStep(false);
      setMode(initialMode);
      onClose();
    }
  }, [saving, onClose, initialMode]);

  const handleSubmit = useCallback(async () => {
    // Validate form
    if (mode === 'replace') {
      if (!formData.sourceRewardId) {
        setError('Please select a source reward to replace');
        return;
      }
      if (!formData.rewardId) {
        setError('Please select a target reward');
        return;
      }
      if (formData.sourceRewardId === formData.rewardId) {
        setError('Source and target rewards must be different');
        return;
      }
    } else if (!formData.rewardId) {
      setError('Please select a reward');
      return;
    }

    if (mode === 'assign') {
      const priority = parseInt(formData.priority, 10);
      if (isNaN(priority) || priority < 1) {
        setError('Priority must be a positive number');
        return;
      }
    }

    // Show confirmation step for destructive actions
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let result: BulkAssignOfferResponse | BulkRemoveOfferResponse | BulkReplaceOfferResponse;

      if (mode === 'assign') {
        const request: BulkAssignOfferRequest = {
          reward_id: formData.rewardId,
          display_priority: parseInt(formData.priority, 10),
          add_to_default_offers: formData.updateDefaultOffers,
        };

        // Add override fields if enabled
        if (formData.useOverrides) {
          request.available_override = formData.availableOverride;
          request.override_reason = formData.overrideReason || null;
          request.show_message_on_card = formData.showMessageOnCard;
        }

        if (formData.applyToAllPartners || !partnerId) {
          result = await partnerService.bulkAssignOfferToAllPartners(request);
        } else {
          result = await partnerService.bulkAssignOfferToPartner(partnerId, request);
        }
      } else if (mode === 'remove') {
        const request: BulkRemoveOfferRequest = {
          reward_id: formData.rewardId,
          remove_from_default_offers: formData.updateDefaultOffers,
        };

        if (formData.applyToAllPartners || !partnerId) {
          result = await partnerService.bulkRemoveOfferFromAllPartners(request);
        } else {
          result = await partnerService.bulkRemoveOfferFromPartner(partnerId, request);
        }
      } else {
        // Replace mode
        const request: BulkReplaceOfferRequest = {
          source_reward_id: formData.sourceRewardId,
          target_reward_id: formData.rewardId,
          update_default_offers: formData.updateDefaultOffers,
          preserve_context: formData.preserveContext,
        };

        // Add override fields if enabled (only when not preserving context or explicitly overriding)
        if (formData.useOverrides) {
          request.available_override = formData.availableOverride;
          request.override_reason = formData.overrideReason || null;
          request.show_message_on_card = formData.showMessageOnCard;
        }

        if (formData.applyToAllPartners || !partnerId) {
          result = await partnerService.bulkReplaceOfferForAllPartners(request);
        } else {
          result = await partnerService.bulkReplaceOfferForPartner(partnerId, request);
        }
      }

      if (onSuccess) {
        onSuccess(result);
      }
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : `Failed to bulk ${mode} offer`;
      setError(errorMessage);
      logger.error(`Failed to bulk ${mode} offer`, { error: err, partnerId, formData });
    } finally {
      setSaving(false);
    }
  }, [formData, partnerId, confirmStep, onSuccess, handleClose, mode]);

  const sourceReward = rewards.find((r) => r.reward_id === formData.sourceRewardId);

  const getActionDescription = () => {
    const target =
      formData.applyToAllPartners || !partnerId
        ? 'ALL active partners'
        : partnerName || `Partner ${partnerId}`;

    const actions: string[] = [];

    if (mode === 'assign') {
      actions.push(
        `Assign "${selectedReward?.reward_name || 'selected reward'}" to all users of ${target}`
      );
      if (formData.updateDefaultOffers) {
        actions.push(
          `Add to default offers for ${formData.applyToAllPartners || !partnerId ? 'all partners' : 'this partner'}`
        );
      }
      if (formData.useOverrides && formData.availableOverride !== null) {
        actions.push(
          `Set availability override to ${formData.availableOverride ? 'AVAILABLE' : 'NOT AVAILABLE'}`
        );
        if (formData.overrideReason) {
          actions.push(`Display message: "${formData.overrideReason}"`);
        }
      }
    } else if (mode === 'remove') {
      actions.push(
        `Remove "${selectedReward?.reward_name || 'selected reward'}" from all users of ${target}`
      );
      if (formData.updateDefaultOffers) {
        actions.push(
          `Remove from default offers for ${formData.applyToAllPartners || !partnerId ? 'all partners' : 'this partner'}`
        );
      }
    } else {
      // Replace mode
      actions.push(
        `Replace "${sourceReward?.reward_name || 'source reward'}" with "${selectedReward?.reward_name || 'target reward'}" for all users of ${target}`
      );
      if (formData.updateDefaultOffers) {
        actions.push(
          `Update default offers for ${formData.applyToAllPartners || !partnerId ? 'all partners' : 'this partner'}`
        );
      }
      if (!formData.preserveContext) {
        actions.push('Replace existing context (overrides will be cleared)');
      }
      if (formData.useOverrides && formData.availableOverride !== null) {
        actions.push(
          `Set availability override to ${formData.availableOverride ? 'AVAILABLE' : 'NOT AVAILABLE'}`
        );
        if (formData.overrideReason) {
          actions.push(`Display message: "${formData.overrideReason}"`);
        }
      }
    }

    return actions;
  };

  const modalTitleMap: Record<OperationMode, string> = {
    assign: 'Bulk Assign Offer',
    remove: 'Bulk Remove Offer',
    replace: 'Bulk Replace Offer',
  };
  const confirmTitleMap: Record<OperationMode, string> = {
    assign: 'Confirm Bulk Assignment',
    remove: 'Confirm Bulk Removal',
    replace: 'Confirm Bulk Replacement',
  };
  const savingTextMap: Record<OperationMode, string> = {
    assign: 'Assigning...',
    remove: 'Removing...',
    replace: 'Replacing...',
  };
  const confirmTextMap: Record<OperationMode, string> = {
    assign: 'Confirm Assignment',
    remove: 'Confirm Removal',
    replace: 'Confirm Replacement',
  };

  const modalTitle = modalTitleMap[mode];
  const confirmTitle = confirmTitleMap[mode];
  const submitText = saving ? savingTextMap[mode] : confirmStep ? confirmTextMap[mode] : 'Continue';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant={confirmStep ? 'danger' : 'primary'}
            onClick={handleSubmit}
            disabled={saving || loadingRewards}
          >
            {submitText}
          </Button>
        </>
      }
    >
      <div className="bulk-assign-modal">
        {error && <div className="bulk-assign-error">{error}</div>}

        {/* Mode Toggle */}
        <div className="mode-toggle">
          <button
            type="button"
            className={`mode-btn ${mode === 'assign' ? 'active' : ''}`}
            onClick={() => handleModeChange('assign')}
            disabled={saving}
          >
            ➕ Assign
          </button>
          <button
            type="button"
            className={`mode-btn remove ${mode === 'remove' ? 'active' : ''}`}
            onClick={() => handleModeChange('remove')}
            disabled={saving}
          >
            ➖ Remove
          </button>
          <button
            type="button"
            className={`mode-btn replace ${mode === 'replace' ? 'active' : ''}`}
            onClick={() => handleModeChange('replace')}
            disabled={saving}
          >
            🔄 Replace
          </button>
        </div>

        {!confirmStep ? (
          <div className="bulk-assign-form">
            {/* Source Reward - only for replace mode */}
            {mode === 'replace' && (
              <Select
                label="Source Reward (to replace)"
                value={formData.sourceRewardId}
                onChange={(value) => handleChange('sourceRewardId', value)}
                options={rewardOptions}
                required
                disabled={loadingRewards}
              />
            )}

            <Select
              label={mode === 'replace' ? 'Target Reward (new)' : 'Reward'}
              value={formData.rewardId}
              onChange={(value) => handleChange('rewardId', value)}
              options={rewardOptions}
              required
              disabled={loadingRewards}
            />

            {mode === 'assign' && (
              <Input
                label="Display Priority"
                type="number"
                value={formData.priority}
                onChange={(value) => handleChange('priority', value)}
                required
              />
            )}

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.updateDefaultOffers}
                  onChange={(e) => handleChange('updateDefaultOffers', e.target.checked)}
                />
                <span>
                  {mode === 'assign'
                    ? 'Add to Default Offers'
                    : mode === 'remove'
                      ? 'Remove from Default Offers'
                      : 'Update Default Offers'}
                </span>
              </label>
              <p className="help-text">
                {mode === 'assign'
                  ? "When enabled, this reward will also be added to the partner's default offers, so new users will automatically receive it."
                  : mode === 'remove'
                    ? "When enabled, this reward will also be removed from the partner's default offers, so new users will no longer receive it automatically."
                    : "When enabled, the source reward will be replaced with the target reward in the partner's default offers."}
              </p>
            </div>

            {/* Preserve Context - only for replace mode */}
            {mode === 'replace' && (
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.preserveContext}
                    onChange={(e) => handleChange('preserveContext', e.target.checked)}
                  />
                  <span>Preserve Existing Context</span>
                </label>
                <p className="help-text">
                  When enabled, existing context_json fields (like availability overrides) will be
                  preserved. When disabled, context will be replaced with the new override settings.
                </p>
              </div>
            )}

            {/* Override Fields - for assign and replace modes */}
            {(mode === 'assign' || mode === 'replace') && (
              <div className="override-section">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.useOverrides}
                      onChange={(e) => handleChange('useOverrides', e.target.checked)}
                    />
                    <span>Set Availability Overrides</span>
                  </label>
                  <p className="help-text">
                    Configure availability overrides at the offer level. This allows you to control
                    whether the reward is available and display custom messages.
                  </p>
                </div>

                {formData.useOverrides && (
                  <div className="override-fields">
                    <Select
                      label="Availability Override"
                      value={
                        formData.availableOverride === null
                          ? ''
                          : formData.availableOverride
                            ? 'true'
                            : 'false'
                      }
                      onChange={(value) => {
                        if (value === '') {
                          handleChange('availableOverride', null as unknown as boolean);
                        } else {
                          handleChange('availableOverride', value === 'true');
                        }
                      }}
                      options={[
                        { value: '', label: 'No override (use partner config)' },
                        { value: 'true', label: 'Available' },
                        { value: 'false', label: 'Not Available' },
                      ]}
                    />

                    <TextArea
                      label="Override Message"
                      value={formData.overrideReason}
                      onChange={(value) => handleChange('overrideReason', value)}
                      rows={2}
                      hint="Message to display when availability is overridden (e.g., 'Processing your card...')"
                    />

                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.showMessageOnCard}
                          onChange={(e) => handleChange('showMessageOnCard', e.target.checked)}
                        />
                        <span>Show Message on Card</span>
                      </label>
                      <p className="help-text">
                        When enabled, the override message will be displayed on the reward card in
                        the list view.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {partnerId && (
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.applyToAllPartners}
                    onChange={(e) => handleChange('applyToAllPartners', e.target.checked)}
                  />
                  <span>Apply to ALL Partners</span>
                </label>
                <p className="help-text warning">
                  ⚠️ This will{' '}
                  {mode === 'assign' ? 'assign' : mode === 'remove' ? 'remove' : 'replace'} the
                  offer {mode === 'assign' ? 'to' : mode === 'remove' ? 'from' : 'for'} users of ALL
                  active partners, not just {partnerName || `Partner ${partnerId}`}.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bulk-assign-confirm">
            <div className="confirm-icon">
              {mode === 'assign' ? '📦' : mode === 'remove' ? '🗑️' : '🔄'}
            </div>
            <h3 className="confirm-title">{confirmTitle}</h3>
            <p className="confirm-subtitle">This action will:</p>
            <ul className="confirm-actions">
              {getActionDescription().map((action, idx) => (
                <li key={idx}>{action}</li>
              ))}
            </ul>
            <p className="confirm-warning">
              {mode === 'assign'
                ? 'Users who already have this offer will not receive duplicates.'
                : mode === 'remove'
                  ? 'This action cannot be undone. Affected users will lose access to this reward.'
                  : 'Existing offers with the source reward will be updated to use the target reward.'}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkAssignOfferModal;
